import React, { useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import {
  REVIEW_FORM_KEYS,
  useSelectedReviewForm,
  useCurrentCompetencyQuestionIndex,
  useCurrentGoalQuestionIndex,
  useCurrentQNAQuestionIndex,
  setCurrentFormKey,
  setCurrentCompetencyQuestionIndex,
  setCurrentGoalQuestionIndex,
  setCurrentQNAQuestionIndex,
} from 'reducers/reviewFormSlice';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import useReviewFormSidebarQueryData from 'screens/performanceReview/hooks/useReviewFormSidebarQueryData';

import { ArrowLeft, ArrowRight } from 'assets/assetsV1/iconComponents';

import { findAndReplaceString } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import {
  sendChangedFormSectionEvent,
  sendClickedPreviousQuestionEvent,
  sendClickedNextQuestionEvent,
} from 'constants/mixpanelHelpers/reviewsEvents';

import { Button } from 'uiToolkitV1/Button';

import REVIEW_FORM_TITLES from '../Sidebar/common/REVIEW_FORM_TITLES';

const calcPreviousQuestionFormIndex = ({ formNames, currentQuestionFormKey, currentQuesIndex }) => {
  const currentFormIndex = formNames.findIndex(({ formName }) => formName === currentQuestionFormKey);

  let hasPreviousQuestion = false;
  let prevQuesFormKey = null;
  let prevQuesIndex = null;

  // early exit if not possible
  if (currentQuesIndex === 0 && currentFormIndex === 0) {
    return { hasPreviousQuestion, prevQuesFormKey, prevQuesIndex };
  }
  hasPreviousQuestion = true;

  if (currentQuesIndex !== 0) {
    prevQuesIndex = currentQuesIndex - 1;
    prevQuesFormKey = currentQuestionFormKey;
  } else {
    prevQuesFormKey = formNames[currentFormIndex - 1].formName;
    prevQuesIndex = 0;
  }

  return { hasPreviousQuestion, prevQuesFormKey, prevQuesIndex };
};

const calcNextQuestionFormIndex = ({
  formNames,
  currentQuestionFormKey,
  currentQuesIndex,
  isLastQuestionOfCurrentForm,
}) => {
  const totalFormsCount = formNames.length;

  const currentFormIndex = formNames.findIndex(({ formName }) => formName === currentQuestionFormKey);

  let hasNextQuestion = false;
  let nextQuesFormKey = null;
  let nextQuesIndex = null;

  // early exit if not possible
  if (isLastQuestionOfCurrentForm && currentFormIndex === totalFormsCount - 1) {
    return { hasNextQuestion, nextQuesFormKey, nextQuesIndex };
  }
  hasNextQuestion = true;

  if (isLastQuestionOfCurrentForm) {
    nextQuesFormKey = formNames[currentFormIndex + 1].formName;
    nextQuesIndex = 0;
  } else {
    nextQuesFormKey = formNames[currentFormIndex].formName;
    nextQuesIndex = currentQuesIndex + 1;
  }

  return { hasNextQuestion, nextQuesFormKey, nextQuesIndex };
};

const getNextButtonText = ({ nextQuesFormKey, currentFormKey }) => {
  let text = '';
  if (nextQuesFormKey === currentFormKey) {
    text = `Next ${REVIEW_FORM_TITLES[currentFormKey].title1}`;
  } else {
    text = `Go to ${REVIEW_FORM_TITLES?.[nextQuesFormKey]?.titlePlural}`;
  }

  return text;
};

const getPrevButtonText = ({ prevQuesFormKey, currentFormKey }) => {
  let text = '';
  if (prevQuesFormKey === currentFormKey) {
    text = isMobileView() ? 'Previous' : `Previous ${REVIEW_FORM_TITLES[currentFormKey].title1}`;
  } else {
    text = isMobileView() ? 'Back' : `Back to ${REVIEW_FORM_TITLES?.[prevQuesFormKey]?.titlePlural}`;
  }

  return text;
};

// the question props is actually not going to be array of question in case of goals and competency,
// it's going to be array of goal and competency which will have a key of quetions.
// But one accordion panel will have all question for a goal or competency, so we are concerned with that only here.
const QuestionsNavigationFooter = ({ questions: currentFormQuestions = [] }) => {
  const dispatch = useDispatch();
  const { reviewId, employeeId } = useReviewFormParams();

  const { keyResultsName } = useOrganisationSettings();

  const divRef = useRef();

  const selectedReviewForm = useSelectedReviewForm();

  const currentCompetencyQuestionIndex = useCurrentCompetencyQuestionIndex();
  const currentGoalQuestionIndex = useCurrentGoalQuestionIndex();
  const currentQNAQuestionIndex = useCurrentQNAQuestionIndex();

  let currentQuesIndex;
  if (selectedReviewForm === REVIEW_FORM_KEYS.COMPETENCY) {
    currentQuesIndex = currentCompetencyQuestionIndex;
  } else {
    currentQuesIndex =
      selectedReviewForm === REVIEW_FORM_KEYS.GOAL ? currentGoalQuestionIndex : currentQNAQuestionIndex;
  }

  const isLastQuestionOfCurrentForm = currentQuesIndex === currentFormQuestions.length - 1;

  const data = useReviewFormSidebarQueryData();

  const { formNames = [] } = data ?? {};

  if (!selectedReviewForm || !data) return null;

  const { hasPreviousQuestion, prevQuesFormKey, prevQuesIndex } = calcPreviousQuestionFormIndex({
    formNames,
    currentQuestionFormKey: selectedReviewForm,
    currentQuesIndex,
  });
  const { hasNextQuestion, nextQuesFormKey, nextQuesIndex } = calcNextQuestionFormIndex({
    formNames,
    currentQuestionFormKey: selectedReviewForm,
    currentQuesIndex,
    isLastQuestionOfCurrentForm,
  });
  const nextButtonText = getNextButtonText({
    formNames,
    nextQuesFormKey,
    currentFormKey: selectedReviewForm,
  });
  const prevButtonText = getPrevButtonText({
    formNames,
    prevQuesFormKey,
    currentFormKey: selectedReviewForm,
  });

  const handleFormNavigation = ({ isNext = true }) => {
    const targetForm = isNext ? nextQuesFormKey : prevQuesFormKey;
    const targetQuesIndex = isNext ? nextQuesIndex : prevQuesIndex;

    dispatch(setCurrentFormKey({ id: targetForm }));

    if (targetForm !== selectedReviewForm) {
      sendChangedFormSectionEvent({
        eventProperties: {
          empId: employeeId,
          sectionName: targetForm,
          source: 'changed from question',
        },
      });
    } else if (isNext) {
      sendClickedNextQuestionEvent({
        eventProperties: {
          reviewCycleId: reviewId,
          empId: employeeId,
        },
      });
    } else {
      sendClickedPreviousQuestionEvent({
        eventProperties: {
          reviewCycleId: reviewId,
          empId: employeeId,
        },
      });
    }

    if (targetForm === REVIEW_FORM_KEYS.GOAL) {
      dispatch(setCurrentGoalQuestionIndex(targetQuesIndex));
    } else if (targetForm === REVIEW_FORM_KEYS.COMPETENCY) {
      dispatch(setCurrentCompetencyQuestionIndex(targetQuesIndex));
    } else {
      dispatch(setCurrentQNAQuestionIndex(targetQuesIndex));
    }
  };

  return (
    <Flex justifyContent="space-between" mt={isMobileView() ? '12px' : 'auto'}>
      <Box>
        <div ref={divRef} />
        {hasPreviousQuestion && (
          <Button
            variant="tertiary"
            StartIcon={ArrowLeft}
            onClick={() => {
              //  AN EXPERIMENTAL SOLUTION FOR FORCEFULLY TRIGGERING THE ON OUTSIDE CLICK STATE OF EDITOR
              divRef.current?.click();
              handleFormNavigation({ isNext: false });
            }}
          >
            {findAndReplaceString(prevButtonText, keyResultsName, 'Goal')}
          </Button>
        )}
      </Box>

      <Box>
        {hasNextQuestion && (
          <Button
            variant="outlined"
            EndIcon={!isMobileView() && ArrowRight}
            onClick={() => {
              //  AN EXPERIMENTAL SOLUTION FOR FORCEFULLY TRIGGERING THE ON OUTSIDE CLICK STATE OF EDITOR
              divRef.current?.click();
              handleFormNavigation({ isNext: true });
            }}
          >
            {findAndReplaceString(nextButtonText, keyResultsName, 'Goal')}
          </Button>
        )}
      </Box>
    </Flex>
  );
};

export default QuestionsNavigationFooter;
